import { Group, Modal, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { RevisionCreatePlanAssignmentRevisionPlanPayload } from 'api/actions/revision-create-plan-assignment-revision-plan/revision-create-plan-assignment-revision-plan-payload';
import Button from 'components/Button';
import RevisionPeriodSelect from 'components/inputs/revision-period-select/RevisionPeriodSelect';
import { useEffect } from 'react';

interface CreateRevisionPlanModalProps {
  opened: boolean;
  deviceName: string;
  loading: boolean;
  onClose: () => void;
  onSubmit: (payload: RevisionCreatePlanAssignmentRevisionPlanPayload) => Promise<void>;
}

interface CreateRevisionPlanFormData {
  description: string;
  revisionPeriod: string;
}

/**
 * Modal for creating a new revision plan during technician plan assignment.
 */
export default function CreateRevisionPlanModal({
  opened,
  deviceName,
  loading,
  onClose,
  onSubmit,
}: CreateRevisionPlanModalProps) {
  const form = useForm<CreateRevisionPlanFormData>({
    initialValues: {
      description: '',
      revisionPeriod: '',
    },
    validate: {
      description: (value) => (value.trim() === '' ? 'Vyplňte názov termínu.' : null),
      revisionPeriod: (value) => (value === '' ? 'Vyberte periódu revízie.' : null),
    },
  });
  const { reset } = form;

  useEffect(() => {
    if (!opened) {
      reset();
    }
  }, [opened, reset]);

  /**
   * Submits the new revision plan request.
   */
  async function submit(values: CreateRevisionPlanFormData) {
    await onSubmit({
      description: values.description.trim(),
      revisionPeriod: Number(values.revisionPeriod),
    });

    form.reset();
  }

  return (
    <Modal
      opened={opened}
      onClose={loading ? () => undefined : onClose}
      title={`Nový termín pre zariadenie ${deviceName}`}
      centered
      size={520}
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
      closeButtonProps={{ loading }}
    >
      <form onSubmit={form.onSubmit(submit)}>
        <Stack spacing={16}>
          <TextInput
            label="Úkon"
            placeholder="Napr. Pravidelná kontrola rozvádzača"
            size="md"
            withAsterisk
            {...form.getInputProps('description')}
          />

          <RevisionPeriodSelect
            label="Perióda"
            placeholder="Vyberte periódu revízie"
            size="md"
            withAsterisk
            value={form.values.revisionPeriod}
            onChange={(value) => form.setFieldValue('revisionPeriod', value ?? '')}
            error={form.errors.revisionPeriod}
          />

          <Group position="right">
            <Button variant="subtle-gray" onClick={onClose} disabled={loading}>
              Zrušiť
            </Button>
            <Button type="submit" loading={loading}>
              Vytvoriť termín
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
